import { useTriggerLoad } from "@redotech/react-util/load";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { isString } from "@redotech/util/type";
import classNames from "classnames";
import { JSXElementConstructor, memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import type { SpacingValue } from "../../theme/box";
import { DisabledTooltip } from "../../tooltip/disabled-tooltip";
import {
  RedoButton,
  RedoButtonSize,
  RedoButtonTheme,
} from "../buttons/redo-button";
import { RedoBaseModal } from "./redo-base-modal";
import * as redoModalCss from "./redo-modal.module.css";

export const redoModalSize = ["xs", "sm", "md", "lg", "xl"] as const;
export type RedoModalSize = (typeof redoModalSize)[number];

export const redoModalTheme = [
  "error",
  "warn",
  "success",
  "info",
  "none",
  "neutral",
  "brand",
] as const;
export type RedoModalTheme = (typeof redoModalTheme)[number];

export const redoModalHeaderIconAlignment = ["above", "inline"] as const;
export type RedoModalHeaderIconAlignment =
  (typeof redoModalHeaderIconAlignment)[number];

export const redoModalButtonPlacement = ["full", "tight"] as const;
export type RedoModalButtonPlacement =
  (typeof redoModalButtonPlacement)[number];

export const redoModalFooterLayout = ["row", "column"] as const;
export type RedoModalFooterLayout = (typeof redoModalFooterLayout)[number];

/**
 * @field pending - the onClick promise will also imply pending. Use this if you're managing the pending state yourself.
 */
export interface RedoModalButtonProps {
  text: string;
  onClick(): void | Promise<void>;
  disabled?: boolean | string;
  pending?: boolean;
  theme?: RedoButtonTheme;
  size?: RedoButtonSize;
  IconLeading?: JSXElementConstructor<unknown>;
  IconTrailing?: JSXElementConstructor<unknown>;
}

/**
 *
 * If you feel the need to add a prop here, consider using <RedoBaseModal> instead
 *
 * If you still feel strongly that a prop is missing that should be on the base modal,
 * please double check with someone on the Arbiter team.
 *
 * @param onModalCloseRequested - Called when x clicked, the user presses escape, or clicks away
 */
export interface RedoModalProps {
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  primaryButton?: RedoModalButtonProps;
  secondaryButton?: RedoModalButtonProps;
  additionalFooterContent?: React.ReactNode;
  children?: ReactNode | undefined;
  onModalCloseRequested: (
    reason?: "xClick" | "escapeKeyDown" | "backdropClick",
  ) => void;
  TitleIcon?: JSXElementConstructor<unknown>;
  headerIconAlignment?: RedoModalHeaderIconAlignment;
  /** Vertical gap between title and subtitle (default xs). */
  headerTextGap?: SpacingValue;
  headerBorder?: boolean;
  theme?: RedoModalTheme;
  hideCloseButton?: boolean;
  modalSize?: RedoModalSize;
  footerBorder?: boolean;
  footerLayout?: RedoModalFooterLayout;
  centerContent?: boolean;
  isOpen?: boolean;
  buttonPlacement?: RedoModalButtonPlacement;
  contentScrollable?: boolean;
  className?: string;
  mainContentClassName?: string;
  noPadding?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  darkMode?: boolean;
  muiClassName?: string;
  uiSelector?: string;
}

export const RedoModal = memo(function RedoModal({
  title,
  subtitle,
  primaryButton,
  secondaryButton,
  additionalFooterContent,
  children,
  onModalCloseRequested,
  TitleIcon,
  headerIconAlignment = "above",
  headerTextGap = "xs",
  headerBorder = false,
  theme = "none",
  hideCloseButton = false,
  modalSize = "md",
  footerBorder = false,
  footerLayout = "row",
  centerContent = false,
  isOpen = true,
  buttonPlacement,
  contentScrollable = false,
  className,
  mainContentClassName,
  noPadding = false,
  hideHeader = false,
  darkMode = false,
  hideFooter = false,
  muiClassName,
  uiSelector,
}: RedoModalProps) {
  return (
    <RedoBaseModal
      darkMode={darkMode}
      isOpen={isOpen}
      muiClassName={muiClassName}
      onModalCloseRequested={onModalCloseRequested}
      otherClasses={[
        modalSizeClasses[modalSize],
        centerContent && redoModalCss.centered,
        className,
      ]}
      uiSelector={uiSelector}
    >
      <>
        {!hideHeader && (
          <RedoModalHeader
            cancelClicked={() => onModalCloseRequested("xClick")}
            centerContent={centerContent}
            headerBorder={headerBorder}
            headerIconAlignment={headerIconAlignment}
            headerTextGap={headerTextGap}
            hideCloseButton={hideCloseButton}
            subtitle={subtitle}
            theme={theme}
            title={title}
            TitleIcon={TitleIcon}
          />
        )}
        {children && (
          <section
            className={classNames(
              redoModalCss.main,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              { [redoModalCss.scrollable]: contentScrollable },
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              { [redoModalCss.noPadding]: noPadding },
              mainContentClassName,
            )}
          >
            {children}
          </section>
        )}
        {!hideFooter && (
          <RedoModalFooter
            additionalContent={additionalFooterContent}
            buttonPlacement={buttonPlacement}
            footerBorder={footerBorder}
            footerLayout={footerLayout}
            primaryButton={primaryButton}
            secondaryButton={secondaryButton}
            theme={theme}
          />
        )}
      </>
    </RedoBaseModal>
  );
});

export const RedoModalHeader = memo(function RedoModalHeader({
  TitleIcon = undefined,
  theme = "none",
  title,
  subtitle,
  hideCloseButton = false,
  centerContent = false,
  headerIconAlignment = "above",
  headerTextGap = "xs",
  headerBorder = false,
  cancelClicked,
}: {
  TitleIcon?: JSXElementConstructor<unknown> | undefined;
  theme?: RedoModalTheme;
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  headerIconAlignment?: RedoModalHeaderIconAlignment;
  headerTextGap?: SpacingValue;
  hideCloseButton?: boolean;
  headerBorder?: boolean;
  centerContent?: boolean;
  cancelClicked(): void;
}) {
  return (
    <header
      className={classNames(
        headerBorder && redoModalCss.border,
        centerContent && redoModalCss.center,
        redoModalCss.header,
        !hideCloseButton && redoModalCss.headerWithClose,
        headerIconAlignmentClasses[headerIconAlignment],
      )}
    >
      {TitleIcon && (
        <div
          className={classNames(
            headerIconAlignmentClasses[headerIconAlignment],
            redoModalCss.titleIcon,
          )}
        >
          <span
            className={classNames(redoModalCss.icon, iconThemeClasses[theme])}
          >
            <TitleIcon />
          </span>
        </div>
      )}
      <Flex dir="column" gap={headerTextGap}>
        <Text fontSize="lg" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
        {subtitle && (
          <Text fontSize="sm" textColor="tertiary">
            {subtitle}
          </Text>
        )}
      </Flex>

      {!hideCloseButton && (
        <RedoButton
          className={redoModalCss.closeButton}
          dataTestId="modal-close-button"
          hierarchy="tertiary"
          IconLeading={CloseIcon}
          onClick={cancelClicked}
          size="md"
        />
      )}
    </header>
  );
});

export const RedoModalFooter = memo(function RedoModalFooter({
  primaryButton,
  secondaryButton,
  additionalContent,
  theme = "none",
  footerBorder,
  buttonPlacement = "tight",
  footerLayout = "row",
}: {
  primaryButton?: RedoModalButtonProps;
  secondaryButton?: RedoModalButtonProps;
  additionalContent?: React.ReactNode;
  theme?: RedoModalTheme;
  footerBorder?: boolean;
  buttonPlacement?: RedoModalButtonPlacement;
  footerLayout?: RedoModalFooterLayout;
}) {
  const [isSubmitting, doSubmit] = useTriggerLoad(async () => {
    await primaryButton?.onClick();
  });

  const submitButton = primaryButton && (
    <DisabledTooltip
      disabledMessage={
        isString(primaryButton.disabled) && primaryButton.disabled
      }
      wrapperClass={redoModalCss.disabledWrapper}
    >
      <RedoButton
        className={redoModalCss.button}
        disabled={!!primaryButton.disabled}
        hierarchy="primary"
        IconLeading={primaryButton.IconLeading}
        IconTrailing={primaryButton.IconTrailing}
        onClick={() => doSubmit()}
        pending={primaryButton.pending ?? isSubmitting.pending}
        size={primaryButton.size ?? "xl"}
        text={primaryButton.text}
        theme={primaryButton.theme ?? redoModalThemeToButtonTheme(theme)}
      />
    </DisabledTooltip>
  );
  const cancelButton = secondaryButton && (
    <DisabledTooltip
      disabledMessage={
        isString(secondaryButton.disabled) && secondaryButton.disabled
      }
      wrapperClass={redoModalCss.disabledWrapper}
    >
      <RedoButton
        className={redoModalCss.button}
        disabled={isSubmitting.pending || !!secondaryButton.disabled}
        hierarchy="secondary"
        IconLeading={secondaryButton.IconLeading}
        IconTrailing={secondaryButton.IconTrailing}
        onClick={secondaryButton.onClick}
        pending={secondaryButton.pending}
        size={secondaryButton.size ?? "xl"}
        text={secondaryButton.text}
        theme={secondaryButton.theme}
      />
    </DisabledTooltip>
  );

  return (
    <footer
      className={classNames(
        redoModalCss.footer,
        footerBorder && redoModalCss.border,
        buttonPlacementClasses[buttonPlacement],
      )}
      style={
        footerLayout === "column" ? { flexDirection: "column" } : undefined
      }
    >
      {additionalContent}
      <div
        className={classNames(
          redoModalCss.footerButtons,
          buttonPlacementClasses[buttonPlacement],
        )}
      >
        {cancelButton}
        {submitButton}
      </div>
    </footer>
  );
});

function redoModalThemeToButtonTheme(theme: RedoModalTheme): RedoButtonTheme {
  switch (theme) {
    case "error":
      return "destructive";
    default:
      return "normal";
  }
}

const modalSizeClasses: Record<RedoModalSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["xs"]: redoModalCss.extraSmall,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["sm"]: redoModalCss.small,
  ["md"]: "",
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["lg"]: redoModalCss.large,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["xl"]: redoModalCss.extraLarge,
};

const iconThemeClasses: Record<RedoModalTheme, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["error"]: redoModalCss.error,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["warn"]: redoModalCss.warn,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["success"]: redoModalCss.success,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["info"]: redoModalCss.info,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["none"]: redoModalCss.none,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["neutral"]: redoModalCss.neutral,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["brand"]: redoModalCss.brand,
};

const headerIconAlignmentClasses = {
  ["above"]: undefined,
  ["inline"]: redoModalCss.inline,
};

const buttonPlacementClasses: Record<RedoModalButtonPlacement, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["full"]: redoModalCss.fill,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  ["tight"]: redoModalCss.tight,
};
